// Amounts are ISO 4217 minor units of their currency (money.ts).
import { approxLabel, LOCAL_TAX, merchDeliveryLabel, storeMoneyMinor as money } from "@libs/platform-wire";
import { orderLinesWords } from "@libs/buy-on-behalf-client/purchase-headline";
import { purchaseTaxWords, type AttemptView, type Purchase, type Total } from "@libs/platform-wire/purchase";
import { deliveryDutiesNote } from "@libs/buy-on-behalf-client/delivery";
import type { Clock } from "@libs/checkout-core";
import type { DeliveryOption } from "@libs/buy-on-behalf-client";
import type {
  FormDraft,
  PurchaseDeliveryController,
  PurchaseTransport,
} from "@libs/buy-on-behalf-client/purchase";
import type { CheckoutChrome } from "../native/sheetButton";

/**
 * What the purchase checkout's two renderers share and nothing else: its
 * props (VendorCheckout.tsx, native; VendorCheckout.web.tsx, app-web's), the
 * order summary's words and the key that starts a checkout again for
 * another buyer. No platform code: both graphs import it.
 */

/** Why the purchase can't go on, as the checkout says it: the funnel's
 *  `checkout_blocked` reasons. */
export type PurchaseBlock = "address_not_uk" | "store_not_in_pounds";

export type VendorCheckoutProps = {
  /** The signed-in buyer's purchase transport. Another transport is another
   *  buyer: the checkout starts again from nothing. */
  transport: PurchaseTransport;
  accessId: string;
  /** The purchase as the sheet read it: the order's summary until the
   *  checkout reads its own. */
  purchase: Purchase;
  /** What the buyer chose on the sheet. */
  lines: { item_id: string; quantity: number }[];
  /** The sheet's delivery (a merch order's): asked again here about the
   *  buyer's own destination, which a start pins. */
  delivery?: PurchaseDeliveryController | null;
  /** The delivery option the buyer chose on the sheet's Delivery step. */
  deliveryCode?: string | null;
  /** The sheet's own id: its estimate's. */
  sheetId: string;
  /** The delivery country the buyer's address named in an earlier checkout
   *  of this sheet, whose read the order was chosen from: read first. Null
   *  for the fan's own. */
  deliverTo?: string | null;
  /** An attempt started before a reload, to pick up again. */
  restoreId?: string | null;
  /** Where to keep the attempt for a reload (`null`: forget it). */
  onAttempt?: (id: string | null) => void;
  newId: () => string;
  /** The header's back: to the ticket selection. Only while nothing is
   *  moving that the buyer may not walk away from. */
  onBack: () => void;
  /** Back to choose again: the order no longer fits the purchase as read,
   *  or the attempt asks the buyer to check it. */
  onReselect?: () => void | Promise<void>;
  /** The sheet draws the one button, what follows it, and keeps the
   *  height: told what they should be. Without it, the checkout draws
   *  them itself. */
  onChrome?: (chrome: CheckoutChrome) => void;
  /** Something is moving the buyer may not walk away from. */
  onHolding?: (holding: boolean) => void;
  /** The confirmed purchase is shown. */
  onSucceeded?: () => void;
  /** Time, for the status's beats; a test moves it by hand. */
  clock?: Clock;
  /** Where the buyer seems to be (the app's region): an address starts
   *  there where the purchase suggests none. */
  buyerCountry?: string | null;
  /** What stops the purchase, told each time it changes to one, as Link in
   *  Bio records it (`checkout_blocked`). */
  onBlocked?: (reason: PurchaseBlock) => void;
  /** What the buyer typed in this sheet's previous checkout (they went back
   *  to choose again): where the form starts. Saved only by a start. */
  initialDraft?: FormDraft;
  /** Told each change to what is typed, and null once a start took it. */
  onDraftChange?: (draft: FormDraft | null) => void;
  /** The purchase as read for the buyer, each time it is read (and the
   *  delivery country it was read for), for the sheet. */
  onPurchase?: (purchase: Purchase | null, read: { deliverTo: string | null }) => void;
};

/** A merch order's delivery as the fan chose it: the option, and whether
 *  the store offered several (then it is named: "Amazon - Premium"; one is
 *  "UK delivery"). */
export type OrderDelivery = {
  option: Pick<DeliveryOption, "name" | "feeMinor" | "dutiesDue">;
  named: boolean;
  country?: string | null;
};

/** The order as the summary says it. */
export type OrderWords = {
  title: string;
  detail: string;
  fee: string | null;
  total: string;
  approx: string | null;
  tax: string | null;
  duties: string | null;
};

/**
 * The order chosen from a purchase, as the summary says it: what is being
 * bought, its fees or its delivery ("UK delivery £5.54", a store's own
 * option by its name), and the total, in the fan's own currency too where
 * the purchase has an estimate. Postage not measured yet says nothing of
 * delivery: neither a fee nor free. `totalMinor`: the order's total where
 * it is known (`orderTotal`), else its items'. `checkout`: the checkout's
 * own total, whose tax it says where known (LIS-1194); else "+ local tax".
 */
export function purchaseOrderOf(
  purchase: Pick<Purchase, "title" | "picker" | "fees" | "delivery" | "currency" | "approx" | "tax" | "goods">,
  lines: readonly { item_id: string; quantity: number }[],
  delivery: OrderDelivery | null,
  totalMinor: number | null,
  checkout: Total | null = null,
): OrderWords {
  const chosen = lines.flatMap((line) => {
    const item = purchase.picker.items.find((candidate) => candidate.id === line.item_id);
    return item ? [{ item, quantity: line.quantity }] : [];
  });
  const items = chosen.reduce((sum, { item, quantity }) => sum + quantity * (item.priceMinor + item.feeMinor), 0);
  const bookingFees = chosen.reduce((sum, { item, quantity }) => sum + quantity * item.feeMinor, 0);
  const total = totalMinor ?? items;
  const fees = Math.max(0, total - items) + bookingFees - (delivery?.option.feeMinor ?? 0);
  let fee: string | null;
  switch (purchase.delivery.kind) {
    case "none":
      fee = fees > 0 ? `Includes ${money(fees, purchase.currency)} fees` : null;
      break;
    // Postage not measured yet, and a download its store emails: no
    // delivery line.
    case "unmeasured":
    case "emailed":
      fee = null;
      break;
    case "ladder":
    case "quoted":
      fee = delivery
        ? `${delivery.named ? delivery.option.name : merchDeliveryLabel(delivery.country)} ${delivery.option.feeMinor > 0 ? money(delivery.option.feeMinor, purchase.currency) : "free"}`
        : null;
      break;
  }
  return {
    title: purchase.title,
    detail: orderLinesWords(chosen.map(({ item, quantity }) => ({ quantity, name: item.name })), purchase),
    fee,
    total: money(total, purchase.currency),
    approx: total > 0 ? approxLabel(total, purchase.currency, purchase.approx) : null,
    tax: purchaseTaxWords(purchase, checkout),
    // The delivery chosen leaves import duties to the fan, or the listing's
    // own postage does, as the purchase says, before its option is priced.
    duties: deliveryDutiesNote(
      delivery?.option ?? (purchase.delivery.kind === "ladder" && purchase.delivery.dutiesDue ? { dutiesDue: true } : null),
    ),
  };
}

/** The delivery an attempt's order pinned, as its summary line says it: a
 *  store's own option by the name the store gave it ("Express · £6.46",
 *  "Express · Free"); the listing's own postage as the sheet says it
 *  ("Delivery £4.29", "Free delivery"). Told apart by how the purchase is
 *  delivered, never by the name. */
export function attemptDeliveryWords(
  delivery: { name: string; feeMinor: number },
  currency: string,
  kind: Purchase["delivery"]["kind"],
): string {
  switch (kind) {
    case "quoted":
      return `${delivery.name} · ${delivery.feeMinor > 0 ? money(delivery.feeMinor, currency) : "Free"}`;
    case "ladder":
    case "none":
    case "unmeasured":
    case "emailed":
      return delivery.feeMinor > 0
        ? `${merchDeliveryLabel(null)} ${money(delivery.feeMinor, currency)}`
        : merchDeliveryLabel(null, true);
  }
}

/** An attempt's own order, as the summary says it: its lines, the
 *  delivery its order pinned (a resumed merch order's), whether that leaves
 *  import duties to the buyer, and its total, with its tax once the buyer
 *  confirmed the taxed total. */
export function attemptOrderOf(
  view: Pick<AttemptView, "offer">,
  purchase: Pick<Purchase, "title" | "approx" | "tax" | "delivery">,
): OrderWords {
  const { offer } = view;
  const delivery = offer.delivery;
  return {
    title: purchase.title,
    detail: orderLinesWords(offer.lines, purchase),
    fee: delivery ? attemptDeliveryWords(delivery, offer.currency, purchase.delivery.kind) : null,
    total: money(offer.totalMinor, offer.currency),
    approx: offer.totalMinor > 0 ? approxLabel(offer.totalMinor, offer.currency, purchase.approx) : null,
    tax: purchase.tax === "added_at_checkout" && offer.taxMinor === null ? LOCAL_TAX : null,
    duties: deliveryDutiesNote(delivery?.dutiesDue ? { dutiesDue: true } : null),
  };
}

/** Another transport is another buyer (or a sign-out and back in): keyed
 *  by it and the access, the checkout starts again from nothing, so nothing
 *  typed, chosen or held for one buyer is ever shown to the next. */
const transportKeys = new WeakMap<PurchaseTransport, number>();
let nextTransportKey = 0;
export const vendorCheckoutKey = (transport: PurchaseTransport, accessId: string) => {
  let key = transportKeys.get(transport);
  if (key === undefined) {
    key = ++nextTransportKey;
    transportKeys.set(transport, key);
  }
  return `${key}:${accessId}`;
};
